/* Serial Seal — arcane motif pack.
 * Extends SS_ART.motif in place. Drawn with SS_ART's own helpers so every
 * motif obeys the material rule: ink is painted AROUND the shine, the holes
 * punched through an inked field ARE the live holographic foil.
 *
 * Six pieces of ORIGINAL fantasy heraldry — a rune, a tome, a gateway, a wyrm,
 * arms, and an elemental array. The genre is the reference; no published game's
 * iconography is. See `pentad` for the note on why its five glyphs are drawn
 * the way they are — that one is the motif most at risk of accidental
 * resemblance, so it is the one argued for in comments.
 */
(function () {
  'use strict';
  var A = window.SS_ART;
  if (!A) return;

  var INK = A.INK, INK3 = A.INK3, L1 = A.L1, L2 = A.L2;
  var r2 = A.r2, rng = A.rng, cut = A.cut, circPath = A.circPath;

  /* ——— path primitives ———
     A hole is a filled REGION, never a stroke, so every punched glyph stroke
     below is built as a real quad. And within one evenodd stack two holes must
     never overlap: the overlap is crossed twice and flips back to solid ink, so
     a rune drawn as overlapping bars loses its own joints. Strokes here abut or
     sit clear of each other by construction. */
  function bar(x, y, w, h) {
    return 'M' + r2(x) + ' ' + r2(y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z';
  }
  function poly(pts) {
    return pts.map(function (p, i) {
      return (i ? 'L' : 'M') + r2(p[0]) + ' ' + r2(p[1]);
    }).join('') + 'Z';
  }
  function dia(x, y, rx, ry) {
    return poly([[x, y - ry], [x + rx, y], [x, y + ry], [x - rx, y]]);
  }
  /* a stroke thickened into a quad — the only way to punch an angled line */
  function seg(x0, y0, x1, y1, w) {
    var dx = x1 - x0, dy = y1 - y0, len = Math.sqrt(dx * dx + dy * dy) || 1;
    var nx = -dy / len * w / 2, ny = dx / len * w / 2;
    return poly([[x0 + nx, y0 + ny], [x1 + nx, y1 + ny], [x1 - nx, y1 - ny], [x0 - nx, y0 - ny]]);
  }
  function lerp(p, q, t) {
    return [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
  }
  /* shrink a quad toward its own centroid — used for keyline rings, which are
     just the same quad twice at two insets */
  function inset(q, t) {
    var c = [(q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4,
             (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4];
    return q.map(function (p) { return lerp(p, c, t); });
  }

  /* ——————— ARCANE MOTIFS ——————— */

  /* an invented rune struck inside a double ring, tick marks radiating off it.
     The glyph is CUT from an inked core so it burns as bare foil rather than
     being painted on — a rune painted pale would vanish into the stock. Its
     strokes are deliberately asymmetric (a high arm hooking down, a low arm
     hooking up, a broken bar, detached pips) so it reads as a made-up mark and
     not as a letter of anyone's alphabet. */
  A.motif.sigil = function (seed) {
    var rnd = rng(seed || 'sigil'), cx = 50, cy = 54, o = '', i, a;

    o += cut([circPath(cx, cy, 44)], INK, 0.4);          // disc = live foil
    o += '<circle cx="' + cx + '" cy="' + cy + '" r="44" fill="none" stroke="' + L2 +
      '" stroke-width="0.6" opacity="0.65"/>';

    /* radiating ticks, every third stepped up so the ring has a beat */
    for (i = 0; i < 36; i++) {
      a = (i / 36) * Math.PI * 2;
      var maj = i % 3 === 0, r0 = 36.5, r1 = maj ? 42.6 : 40.2;
      o += '<path d="M' + r2(cx + Math.cos(a) * r0) + ' ' + r2(cy + Math.sin(a) * r0) +
        'L' + r2(cx + Math.cos(a) * r1) + ' ' + r2(cy + Math.sin(a) * r1) +
        '" stroke="' + INK + '" stroke-width="' + (maj ? 1.9 : 0.85) + '"/>';
    }
    o += '<circle cx="' + cx + '" cy="' + cy + '" r="34.5" fill="none" stroke="' + INK + '" stroke-width="2.4"/>';
    o += '<circle cx="' + cx + '" cy="' + cy + '" r="30.5" fill="none" stroke="' + INK + '" stroke-width="1.2"/>';
    o += '<circle cx="' + cx + '" cy="' + cy + '" r="27" fill="none" stroke="{{color_hex}}" stroke-width="1.4"/>';

    /* the mark itself. Canted a few degrees off vertical — a rune that stands
       perfectly upright starts to read as typography. The core is a circle, so
       rotating the whole group only moves the glyph. */
    var cant = r2(-8 + rnd() * 16);
    var glyph = [
      bar(48.2, 38, 3.6, 28),          // stem
      bar(37, 43, 11.2, 3.6),          // high arm, struck left
      bar(37, 46.6, 3.6, 7.9),         // ...hooking down
      bar(51.8, 57.4, 11.2, 3.6),      // low arm, struck right
      bar(59.4, 49.5, 3.6, 7.9),       // ...hooking up
      bar(41.5, 62.4, 6.7, 2.6),       // bar broken across the stem, both halves
      bar(51.8, 62.4, 6.7, 2.6),       //    abutting it so no hole overlaps
      circPath(41.5, 34.5, 2.1),       // pips, diagonally opposed
      circPath(58.5, 69.5, 2.1),
      seg(36, 66, 41, 73, 3.2)         // detached slash, low left
    ];
    o += '<g transform="rotate(' + cant + ' ' + cx + ' ' + cy + ')">';
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(cx, cy, 23.5) + ' ' +
      glyph.join(' ') + '"/>';
    o += '</g>';

    /* exergue — a plain inked rule so the disc has ground under it */
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="M16 102h68v10h-68Z ' +
      'M22 105.4h26v3.2h-26Z M52 105.4h14v3.2h-14Z"/>';
    o += '<path d="M20 101h60" stroke="{{color_hex}}" stroke-width="1.1" opacity="0.9"/>';
    return o;
  };

  /* a closed spellbook seen three-quarter: cover toward us, the fore-edge page
     block turned away to the right, clasp strapped over both. The page edges
     are foil slits cut through the block — a page block drawn as pale lines on
     dark would print as mush, whereas slits of bare stock actually fan. */
  A.motif.grimoire = function (seed) {
    var rnd = rng(seed || 'grimoire'), o = '';
    var Ac = [23, 21], Bc = [71, 15], Cc = [75, 93], Dc = [27, 101];   // cover
    var Ec = [86, 24], Fc = [90, 100];                                  // fore-edge outer
    var Gc = [42, 108];                                                 // tail, dropped from D

    /* whole book silhouette punched to foil first, then its faces inked back
       on top — the same order the slab motif uses to get a lit edge */
    o += cut([poly([Ac, Bc, Ec, Fc, Gc, Dc])], INK, 0.42);

    /* tail (the bottom edge of the block) sits furthest from the light */
    o += '<path fill="' + INK3 + '" d="' + poly([Dc, Cc, Fc, Gc]) + '"/>';

    /* fore-edge: inked face, pages cut out of it. Slits run with the height of
       the book and step across its thickness, so they lean as the face does. */
    function pb(u, v) {
      return [Bc[0] + u * (Ec[0] - Bc[0]) + v * (Cc[0] - Bc[0]),
              Bc[1] + u * (Ec[1] - Bc[1]) + v * (Cc[1] - Bc[1])];
    }
    var pages = [], i, u;
    for (i = 0; i < 6; i++) {
      u = 0.11 + i * 0.145;
      pages.push(poly([pb(u, 0.07), pb(u + 0.055, 0.07), pb(u + 0.055, 0.93), pb(u, 0.93)]));
    }
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + poly([Bc, Ec, Fc, Cc]) + ' ' +
      pages.join(' ') + '"/>';

    /* cover: one evenodd stack — keyline ring, corner bosses and the emblem all
       punched clear through the board. Nested holes flip parity cleanly, which
       is why the emblem can be a ring with a lozenge eye inside it. */
    var cov = [Ac, Bc, Cc, Dc];
    var kOut = inset(cov, 0.06), kIn = inset(cov, 0.088);
    var ex = 48.5, ey = 51, holes = [poly(kOut), poly(kIn)];
    inset(cov, 0.16).forEach(function (p) {                 // corner bosses
      holes.push(bar(p[0] - 2.2, p[1] - 2.2, 4.4, 4.4));
    });
    holes.push(circPath(ex, ey, 12.4), circPath(ex, ey, 9.2), dia(ex, ey, 5.4, 6.4));
    /* the clasp's latch end is CUT, not painted: an ink strap on an ink board
       is invisible, and the catch is the one bright fitting on the whole book.
       It stops short of the keyline ring, because two holes that overlap flip
       back to ink and would notch the strap where it crossed. */
    holes.push(seg(40, 80.6, 69, 77.4, 7.2));
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + poly(cov) + ' ' + holes.join(' ') + '"/>';
    o += '<path d="' + poly(cov) + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
    o += '<circle cx="' + ex + '" cy="' + ey + '" r="2.3" fill="' + INK + '"/>';   // boss in the eye

    /* raised band across the head of the board */
    o += '<path fill="' + INK + '" d="' + seg(29.5, 32, 68, 27, 2.2) + '"/>';

    /* the rest of the strap wraps the fore-edge, where ink over the cut page
       slits is what makes it read. It rides the lower third — a strap across
       the emblem turns a spellbook into a parcel. */
    o += '<path fill="' + INK + '" d="' + seg(69, 77.4, 76, 76.6, 7.4) + ' ' +
      seg(76, 76.6, pb(0.94, 0.76)[0], pb(0.94, 0.76)[1], 7.4) + '"/>';
    /* buckle: an ink ring on the cut strap, its tongue-hole back to bare foil */
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + dia(64, 77.8, 6.2, 5.2) + ' ' +
      dia(64, 77.8, 2.6, 2.2) + '"/>';
    o += '<path d="' + dia(64, 77.8, 8.4, 7) + '" fill="none" stroke="' + L2 +
      '" stroke-width="0.6" opacity="0.7"/>';

    /* ribbon marker, dropped out of the block with a forked tail */
    o += '<path fill="' + INK + '" d="M78 95L86.4 98.6L83.4 116L78 108.6L71.6 112.6Z"/>';
    o += '<path d="M79 97.8L84 99.9" stroke="{{color_hex}}" stroke-width="1.4"/>';

    for (i = 0; i < 7; i++) {                               // dust in the smoked ground
      o += '<circle cx="' + r2(6 + rnd() * 12) + '" cy="' + r2(14 + rnd() * 92) +
        '" r="' + r2(0.6 + rnd() * 1.1) + '" fill="' + L1 + '" opacity="0.45"/>';
    }
    return o;
  };

  /* a standing-stone gateway. The whole frame is one inked mass and everything
     that should shine is punched out of it: the plane inside the arch, the
     mortar joints that carve the stones apart, and the runes down the jambs.
     Drawing the joints as pale lines would have lost them at 19mm; as gaps of
     bare stock they cut hard. */
  A.motif.portal = function (seed) {
    var rnd = rng(seed || 'portal'), o = '', i, a;
    var CX = 50, CY = 46, RO = 36, RI = 16;

    var outer = 'M14 106V' + CY + 'A' + RO + ' ' + RO + ' 0 0 1 86 ' + CY + 'V106Z';
    var open = 'M34 106V' + CY + 'A' + RI + ' ' + RI + ' 0 0 1 66 ' + CY + 'V106Z';
    var holes = [open];

    /* course joints across each jamb, stopping just short of the opening */
    [60, 70, 80, 90, 100].forEach(function (y) {
      holes.push(bar(14, y, 19.4, 1.2), bar(66.6, y, 19.4, 1.2));
    });
    /* voussoir joints radiating through the arch head — the two either side of
       vertical leave a keystone standing at the crown */
    [195, 225, 255, 285, 315, 345].forEach(function (deg) {
      var t = deg * Math.PI / 180;
      holes.push(seg(CX + Math.cos(t) * (RI + 0.4), CY + Math.sin(t) * (RI + 0.4),
                     CX + Math.cos(t) * (RO - 0.4), CY + Math.sin(t) * (RO - 0.4), 1.3));
    });
    holes.push(dia(CX, CY - 26, 2.6, 3.4));                 // keystone pip

    /* jamb runes — broken strokes with air between them, so no two holes touch
       and none of them settles into a readable letter */
    [[24, 65], [24, 85], [76, 75], [76, 95]].forEach(function (p, k) {
      var x = p[0], y = p[1];
      if (k % 2) {
        holes.push(seg(x - 3.6, y - 3, x + 3.6, y - 3, 2.2),
                   seg(x - 2.6, y + 0.4, x + 1.4, y + 3.4, 2.2),
                   circPath(x + 3.4, y + 2.6, 1.1));
      } else {
        holes.push(seg(x - 2.8, y + 3, x + 1.4, y - 3, 2.4),
                   seg(x + 2.4, y - 2.4, x + 3.6, y + 1.6, 2.0),
                   circPath(x - 3.4, y - 2.8, 1.1));
      }
    });
    /* smoke the ground and lift the whole gateway out of it as foil FIRST, then
       ink the stonework back on at full strength. Punching the joints out of a
       single smoked field instead would leave frame and background at the same
       tone, and the arch would stop reading as an object standing in front. */
    o += cut([outer], INK, 0.3);
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + outer + ' ' + holes.join(' ') + '"/>';

    /* the plane: bare foil with the faintest ink swell on it, so it reads as a
       surface under tension rather than an empty cut-out */
    [[9, 20], [13.5, 27], [17, 33]].forEach(function (e, k) {
      o += '<ellipse cx="50" cy="70" rx="' + e[0] + '" ry="' + e[1] + '" fill="none" stroke="' +
        (k === 1 ? L2 : INK) + '" stroke-width="' + (k === 1 ? 0.55 : 0.9) + '" opacity="0.6"/>';
    });
    o += '<path d="M50 40V100" stroke="{{color_hex}}" stroke-width="1.3" opacity="0.85"/>';
    for (i = 0; i < 10; i++) {                              // motes drifting in the plane
      a = rnd() * Math.PI * 2;
      var rr = rnd() * 13;
      o += '<circle cx="' + r2(50 + Math.cos(a) * rr) + '" cy="' + r2(70 + Math.sin(a) * rr * 2.1) +
        '" r="' + r2(0.6 + rnd() * 1.0) + '" fill="' + INK + '" opacity="' + r2(0.4 + rnd() * 0.4) + '"/>';
    }
    o += '<path d="' + outer + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
    o += '<path d="M30 106V' + CY + 'A20 20 0 0 1 70 ' + CY + 'V106" fill="none" stroke="' + L2 +
      '" stroke-width="0.55" opacity="0.6"/>';

    /* plinth the jambs stand on, slotted to foil */
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="M8 106H92L96 115H4Z ' +
      'M18 109h30v2.6h-30Z M54 109h20v2.6h-20Z"/>';
    return o;
  };

  /* a wyrm's head in profile — heraldry, not illustration: one bold ink
     silhouette laid straight onto a foil roundel, articulated only by what is
     punched back out of it (eye, nostril, a run of neck scales). Ink on
     bare stock is the highest-contrast thing this press can do, so the charge
     is the ink and the field is the shine. Our own dragon. */
  /* A serpent-drake wound around itself. REDRAWN: the first version was a head
     in profile with two standing points on the crown, and standing points read
     as EARS at any size — it came back looking like a wolf, not a dragon. A
     coil cannot be misread: the spiral silhouette says serpent before any
     detail resolves, and the silhouette is all that survives at 19mm. The body
     is one evenodd stack — outer edge out, inner edge back — with the scales
     punched through it, so every scale is bare foil on an inked back. */
  A.motif.wyrm = function (seed) {
    var rnd = rng(seed || 'wyrm'), o = '';
    var CX = 50, CY = 62, TURNS = 1.34, N = 96;
    function pt(t) {
      var a = -Math.PI / 2 + t * Math.PI * 2 * TURNS, r = 33.5 - t * 23.5;
      return [CX + Math.cos(a) * r, CY + Math.sin(a) * r];
    }
    function halfw(t) { return 8.2 - t * 6.0; }

    var outer = [], inner = [], mid = [];
    for (var i = 0; i <= N; i++) {
      var t = i / N, c = pt(t), q = pt(Math.min(1, t + 0.004));
      var dx = q[0] - c[0], dy = q[1] - c[1], L = Math.sqrt(dx * dx + dy * dy) || 1;
      var nx = -dy / L, ny = dx / L, w = halfw(t);
      outer.push([c[0] + nx * w, c[1] + ny * w]);
      inner.push([c[0] - nx * w, c[1] - ny * w]);
      mid.push([c[0], c[1], nx, ny, w]);
    }
    var bodyPath = poly(outer.concat(inner.slice().reverse()));

    /* scales, punched. Spaced along the coil so they never touch: two holes
       that overlap inside one evenodd stack cancel back to solid ink. */
    var scales = [];
    for (var k = 8; k < N - 5; k += 7) {
      var m = mid[k], w2 = m[4] * 0.5;
      scales.push(dia(m[0], m[1], w2, w2 * 0.85));
    }
    o += cut([circPath(CX, CY, 46)], INK, 0.4);
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + bodyPath + ' ' +
         scales.join(' ') + '"/>';

    /* NO SEPARATE HEAD, deliberately — third attempt and the right one. Two
       earlier drafts bolted a profile skull onto the coil: the first read as a
       wolf (standing horns are ears at any size), the second spilled outside
       the keyline, and clamping it back inside flattened it to a blob. A
       roundel this full has nowhere to put a head. So the coil's thick end IS
       the head: it already tapers like a serpent, and an eye plus a fang line
       is the whole vocabulary needed to say which end is which. Everything
       stays inside the band by construction, because it is the band. */
    var hm = mid[3], hw = hm[4];
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' +
      poly([[hm[0] + hm[2] * hw * 0.15 - hm[3] * hw * 0.95,
             hm[1] + hm[3] * hw * 0.15 + hm[2] * hw * 0.95],
            [hm[0] + hm[2] * hw * 0.95, hm[1] + hm[3] * hw * 0.95],
            [hm[0] - hm[2] * hw * 0.95, hm[1] - hm[3] * hw * 0.95]]) + '"/>';
    /* the eye, punched clean through the body so it flashes foil */
    var em = mid[6];
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' +
      poly([[em[0] - em[2] * em[4], em[1] - em[3] * em[4]],
            [em[0] + em[2] * em[4], em[1] + em[3] * em[4]],
            [em[0] + em[2] * em[4] - em[3] * 9, em[1] + em[3] * em[4] + em[2] * 9],
            [em[0] - em[2] * em[4] - em[3] * 9, em[1] - em[3] * em[4] + em[2] * 9]]) +
      ' ' + dia(em[0] - em[3] * 3.4, em[1] + em[2] * 3.4, 2.9, 2.0) + '"/>';
    /* fangs along the inner edge, reading as an open jaw at the coil's mouth */
    for (var f = 0; f < 4; f++) {
      var fm = mid[1 + f * 2];
      var bx = fm[0] - fm[2] * fm[4] * 0.98, by = fm[1] - fm[3] * fm[4] * 0.98;
      o += '<path fill="{{color_hex}}" opacity="0.95" d="' + poly([
        [bx - fm[3] * 1.5, by + fm[2] * 1.5],
        [bx + fm[3] * 1.5, by - fm[2] * 1.5],
        [bx - fm[2] * 3.4, by - fm[3] * 3.4]]) + '"/>';
    }
    o += '<path d="' + circPath(CX, CY, 46) + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
    for (var e = 0; e < 26; e++) {
      var ea = rnd() * Math.PI * 2, er = 47 + rnd() * 8;
      var ex = CX + Math.cos(ea) * er, ey = CY + Math.sin(ea) * er;
      if (ex < 3 || ex > 97 || ey < 3 || ey > 114) continue;
      o += '<circle cx="' + r2(ex) + '" cy="' + r2(ey) + '" r="' + r2(0.7 + rnd() * 1.0) +
        '" fill="' + INK + '" opacity="' + r2(0.4 + rnd() * 0.4) + '"/>';
    }
    return o;
  };


  /* A greatsword point-down on a shield. REDRAWN: the first version laid the
     sword on the bend BEHIND the shield, and because the shield field was bare
     foil the two halves of the blade could never meet across it — at seal size
     it read as a small dagger at lower-left and a floating spearhead at upper
     right, two objects instead of one weapon. Inverting the values fixes it:
     the shield is INKED and the entire sword is punched THROUGH it as one
     continuous hole, so a single bright blade sits on a dark field. */
  A.motif.blade = function (seed) {
    var rnd = rng(seed || 'blade'), o = '';
    var shield = 'M50 14L84 24V58C84 82 68 96 50 105C32 96 16 82 16 58V24Z';
    o += cut([shield], INK, 0.34);
    var sword = poly([[47.2, 26], [52.8, 26], [52.8, 76], [50, 88], [47.2, 76]]);
    var guard = poly([[32, 21.5], [68, 21.5], [68, 26], [32, 26]]);
    var grip = poly([[47.7, 13], [52.3, 13], [52.3, 21.5], [47.7, 21.5]]);
    var pommel = circPath(50, 11, 3.5);
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + shield + ' ' + sword +
         ' ' + guard + ' ' + grip + ' ' + pommel + '"/>';
    o += '<path d="' + shield + '" fill="none" stroke="{{color_hex}}" stroke-width="1.7"/>';
    /* runes down the fuller — ink marks ON the bright blade. This is the one
       place in the motif where dark-on-light is the correct direction. */
    for (var i = 0; i < 9; i++) {
      var y = 31 + i * 5.0;
      o += '<path fill="' + INK + '" opacity="0.92" d="' +
        (i % 3 === 0 ? bar(48.7, y, 2.6, 1.4)
          : i % 3 === 1 ? dia(50, y + 0.7, 1.4, 1.9)
            : bar(49.3, y, 1.4, 2.1)) + '"/>';
    }
    for (var s = -1; s <= 1; s += 2) {
      for (var j = 0; j < 6; j++) {
        var ly = 48 + j * 8.0, lx = 50 + s * (23 + j * 0.8);
        o += '<ellipse cx="' + r2(lx) + '" cy="' + r2(ly) + '" rx="4.4" ry="2.0" fill="' + INK +
          '" transform="rotate(' + (s * (30 + j * 4)) + ' ' + r2(lx) + ' ' + r2(ly) + ')"/>';
      }
    }
    o += '<path fill="' + INK + '" d="M12 104h76l-8 7 8 7H12l8-7Z"/>';
    o += '<path d="M17 108h66" stroke="{{color_hex}}" stroke-width="1.15" opacity="0.9"/>';
    for (var k = 0; k < 16; k++) {
      var sx = 8 + rnd() * 84, sy = 16 + rnd() * 80;
      if (Math.abs(sx - 50) < 13 && sy < 92) continue;   /* never speckle the blade */
      o += '<circle cx="' + r2(sx) + '" cy="' + r2(sy) + '" r="' + r2(0.6 + rnd() * 0.9) +
        '" fill="' + INK + '" opacity="' + r2(0.35 + rnd() * 0.35) + '"/>';
    }
    return o;
  };


  /* five elemental glyphs on a pentagon, tied by ley-lines.
   *
   * ⚠ Every glyph here is deliberately GEOMETRIC and non-representational,
   * because the obvious five for this slot are somebody else's marks. What is
   * drawn, and why each is clear of the thing it must not be:
   *   1 chevron stack   — three nested directional chevrons. Linear and
   *                       pointed, with no disc and no radial symmetry, so it
   *                       is nothing like a rayed sun.
   *   2 spiral node     — square-cornered gapped rings coiling into a dot.
   *                       Rectilinear and closed; a droplet is a round belly
   *                       tapering to one point, which this has nowhere.
   *   3 bisected diamond— a hollow half over a solid half, split by a rule.
   *                       A pure quadrilateral: no cranium, no sockets, no jaw,
   *                       nothing organic to read as a skull.
   *   4 triple bar      — three graduated bars with a riser on the top one.
   *                       Entirely orthogonal and flat, with no round mass and
   *                       no curve, so it cannot be taken for a fireball.
   *   5 crescent lattice— a lying crescent laddered by three struts. Has no
   *                       trunk, no canopy and no ground line; it reads as an
   *                       instrument, not a tree.
   * The arrangement is a pentagon because the brief asks for one; the meaning
   * lives in the glyphs, and the glyphs are ours. */
  A.motif.pentad = function (seed) {
    var rnd = rng(seed || 'pentad'), o = '', i;
    var CX = 50, CY = 58, R = 33, NR = 12.5;
    var v = [-90, -18, 54, 126, 198].map(function (d) {
      var t = d * Math.PI / 180;
      return [CX + Math.cos(t) * R, CY + Math.sin(t) * R];
    });

    /* smoked ground with the five node seats punched clean through it, so the
       glyph cut inside each disc lands on BARE foil. Smoking the whole window
       and punching nothing leaves the glyphs opening onto tinted stock, and
       they lose most of their flash. */
    o += cut(v.map(function (p) { return circPath(p[0], p[1], NR + 2.2); }), INK, 0.32);

    /* tick ring runs on an ELLIPSE, not a circle — a circle wide enough to
       reach the top and bottom of a 100×117 window gets its 3 and 9 o'clock
       ticks trimmed off by the trim edge */
    for (i = 0; i < 40; i++) {
      var a = (i / 40) * Math.PI * 2, maj = i % 5 === 0;
      o += '<path d="M' + r2(CX + Math.cos(a) * 44) + ' ' + r2(CY + Math.sin(a) * 50) +
        'L' + r2(CX + Math.cos(a) * (maj ? 48.5 : 46.4)) + ' ' +
        r2(CY + Math.sin(a) * (maj ? 55.5 : 53)) +
        '" stroke="' + INK + '" stroke-width="' + (maj ? 1.6 : 0.7) + '"/>';
    }
    /* ley-lines: the RING only — the five edges of the pentagon, and nothing
       across it.
       ⚠ Do NOT add the v[i]→v[i+2] chords back. Five marks on a pentagon joined
       by an outer ring AND an inner star is the published colour-wheel diagram
       this motif has to stay clear of; the brief in ss-templates.js says draw
       the ring, not the wheel. Original glyphs do not save a borrowed diagram.
       Each edge also stops at the node seat rather than running under it: the
       glyphs are HOLES, so any line crossing a disc shows straight through the
       cut-out and prints as a slash across the glyph. */
    for (i = 0; i < 5; i++) {
      var p0 = v[i], p1 = v[(i + 1) % 5];
      var ex = p1[0] - p0[0], ey = p1[1] - p0[1];
      var el = Math.sqrt(ex * ex + ey * ey) || 1, ct = (NR + 2.2) / el;
      o += '<path d="M' + r2(p0[0] + ex * ct) + ' ' + r2(p0[1] + ey * ct) +
        'L' + r2(p1[0] - ex * ct) + ' ' + r2(p1[1] - ey * ct) +
        '" stroke="' + INK + '" stroke-width="1.5"/>';
    }

    /* ——— the five glyphs, each in its own node's local frame ——— */
    var glyph = [];

    glyph.push([-5.5, 0, 5.5].map(function (d) {            // 1 — chevron stack
      return poly([[-6.5, 2.2 + d], [0, -2.3 + d], [6.5, 2.2 + d],
                   [6.5, 4.8 + d], [0, 0.3 + d], [-6.5, 4.8 + d]]);
    }));

    /* 2 — square coil. A gapped ring traced outer-edge-round then back along
       the inner edge; the gaps face opposite ways so the rings read as one
       continuous winding rather than as nested boxes. */
    function coil(rad, th, gap, turns) {
      var p = [[rad, -gap], [rad, -rad], [-rad, -rad], [-rad, rad], [rad, rad], [rad, gap],
               [rad - th, gap], [rad - th, rad - th], [-(rad - th), rad - th],
               [-(rad - th), -(rad - th)], [rad - th, -(rad - th)], [rad - th, -gap]];
      var c = Math.cos(turns * Math.PI / 2), s = Math.sin(turns * Math.PI / 2);
      return poly(p.map(function (q) { return [q[0] * c - q[1] * s, q[0] * s + q[1] * c]; }));
    }
    glyph.push([coil(6.6, 1.9, 1.6, 0), coil(3.7, 1.7, 1.4, 2), circPath(0, 0, 1.1)]);

    glyph.push([                                            // 3 — bisected diamond
      poly([[0, -7.6], [5.7, -0.7], [-5.7, -0.7]]),
      poly([[0, -4.5], [2.8, -1.1], [-2.8, -1.1]]),
      poly([[0, 7.6], [5.7, 0.9], [-5.7, 0.9]])
    ]);

    glyph.push([                                            // 4 — triple bar + riser
      bar(-6.6, -5.6, 13.2, 2.3), bar(-6.6, -0.4, 9.6, 2.3), bar(-6.6, 4.8, 6.2, 2.3),
      bar(-6.6, -8.8, 2.3, 3.2)
    ]);

    /* 5 — crescent as two arcs (a circle minus an offset circle would leak the
       offset circle's outside edge back in as a second hole under evenodd) */
    glyph.push(['M4.03 -6.44A7.6 7.6 0 1 0 4.03 6.44A6.6 6.6 0 1 1 4.03 -6.44Z']);

    /* struts are painted ON the foil crescent rather than punched alongside it —
       two holes that overlap cancel back to ink, and the lattice has to cross
       the arc, not interrupt it. Radial and hairline: parallel bars over a fat
       crescent close the whole node up into one blob at seal size. */
    var extra = ['', '', '', '', [150, 180, 210].map(function (deg) {
      var t = deg * Math.PI / 180;
      return '<path fill="' + INK + '" d="' +
        seg(Math.cos(t) * 3, Math.sin(t) * 3, Math.cos(t) * 9.4, Math.sin(t) * 9.4, 1.2) + '"/>';
    }).join('')];
    /* the crescent lies horns-DOWN. Horns-up plus three struts across it read
       as a mouth in a round node, and a smiling face is not an element. */
    var spin = [0, 0, 0, 0, 90];

    for (i = 0; i < 5; i++) {
      o += '<circle cx="' + r2(v[i][0]) + '" cy="' + r2(v[i][1]) + '" r="' + r2(NR + 2.2) +
        '" fill="none" stroke="' + L2 + '" stroke-width="0.6" opacity="0.6"/>';
      o += '<g transform="translate(' + r2(v[i][0]) + ' ' + r2(v[i][1]) + ')' +
        (spin[i] ? ' rotate(' + spin[i] + ')' : '') + '">';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(0, 0, NR) + ' ' +
        glyph[i].join(' ') + '"/>' + extra[i] + '</g>';
    }

    /* hub where the lines meet — inked, with a foil pentagon punched in it */
    var hub = v.map(function (p) { return [CX + (p[0] - CX) * 0.155, CY + (p[1] - CY) * 0.155]; });
    o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(CX, CY, 7.4) + ' ' +
      poly(hub) + '"/>';
    o += '<circle cx="' + CX + '" cy="' + CY + '" r="9.6" fill="none" stroke="{{color_hex}}" stroke-width="1.4"/>';

    for (i = 0; i < 14; i++) {                              // sparse charge in the open field
      var aa = rnd() * Math.PI * 2, rr = 20 + rnd() * 30;
      o += '<circle cx="' + r2(CX + Math.cos(aa) * rr) + '" cy="' + r2(CY + Math.sin(aa) * rr) +
        '" r="' + r2(0.5 + rnd() * 0.9) + '" fill="' + L1 + '" opacity="' + r2(0.3 + rnd() * 0.35) + '"/>';
    }
    return o;
  };
})();
